/**
 * Streaming service for OpenAI content generation
 */

import { getAccessToken } from '../utils/authUtils';
import {
  getOrCreateSession as getOrCreateAnonymousSession,
  getAnonymousHeaders,
  recordLocalBlogGeneration,
} from './anonymousSessionService';

const API_BASE_URL = process.env.REACT_APP_CON_API_BASE_URL || 'http://localhost:3001';

/**
 * Gets authorization headers for API calls.
 *
 * Prefers a Bearer token from MSAL when the user is signed in. Falls back to
 * an anonymous trial header (auto-creating the trial session on first use)
 * when no account is present so unauthenticated visitors can still generate
 * the two free blogs.
 *
 * @returns {Promise<Object>} - Authorization headers
 */
const getAuthHeaders = async () => {
  try {
    const token = await getAccessToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    // Expected when the visitor hasn't signed in. Fall back to the anonymous
    // trial session so the free-blog flow works without MSAL.
    if (process.env.NODE_ENV === 'development') {
      console.info('Falling back to anonymous session for streaming:', error?.message);
    }
    try {
      let anonHeaders = getAnonymousHeaders();
      if (!anonHeaders) {
        await getOrCreateAnonymousSession();
        anonHeaders = getAnonymousHeaders();
      }
      if (anonHeaders) {
        return {
          ...anonHeaders,
          'Content-Type': 'application/json',
        };
      }
    } catch (anonErr) {
      console.warn('Failed to acquire anonymous session for streaming:', anonErr);
    }
    return {
      'Content-Type': 'application/json',
    };
  }
};

/**
 * Parse an anonymous-trial-limit response from the server so callers can
 * trigger the signup modal.
 */
function buildAnonymousLimitError(body) {
  const err = new Error(body?.error || 'Anonymous trial limit reached');
  err.isAnonymousLimit = true;
  err.code = body?.code || 'ANON_LIMIT_REACHED';
  err.blogsGenerated = body?.blogsGenerated ?? null;
  err.freeLimit = body?.freeLimit ?? null;
  return err;
}

/**
 * Creates an EventSource connection for streaming blog content.
 * @param {string} h1 - The H1 heading for blog generation
 * @param {Object} options - Options object containing callbacks and brand voice parameters
 * @param {Function} options.onContent - Callback for content chunks
 * @param {Function} options.onComplete - Callback for completion
 * @param {Function} options.onError - Callback for errors
 * @param {Object} options.brandVoice - Brand voice fields plus optional blog_language ('en'|'nl')
 * @returns {EventSource} - The EventSource instance
 */
export const streamBlogGeneration = async (h1, { onContent, onComplete, onError, brandVoice = {}, priorCompletedGenerations = 0, trendData = '' }) => {
  const headers = await getAuthHeaders();
  const isAnonymous = Boolean(headers['X-Anonymous-Session']) && !headers['Authorization'];

  // Build request payload with h1 and optional brand voice parameters
  const payload = {
    h1,
    priorCompletedGenerations,
    trend_data: trendData,
    ...brandVoice
  };

  const wrappedOnComplete = (content) => {
    if (isAnonymous) {
      try { recordLocalBlogGeneration(); } catch { /* ignore */ }
    }
    if (onComplete) onComplete(content);
  };

  return createStreamConnection(`${API_BASE_URL}/api/seo/blog/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  }, { onContent, onComplete: wrappedOnComplete, onError });
};

/**
 * Creates an EventSource connection for streaming enhancement content
 * @param {Object} enhancement - The enhancement object with type and prompt
 * @param {string} blogContext - Current blog content for context
 * @param {Object} callbacks - Callback functions
 * @returns {EventSource} - The EventSource instance
 */
export const streamEnhancementGeneration = async (enhancement, blogContext, { onContent, onComplete, onError }) => {
  const prompt = createEnhancementPrompt(enhancement, blogContext);
  const headers = await getAuthHeaders();
  
  return createStreamConnection(`${API_BASE_URL}/api/chat/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messages: [
        {
          role: 'system',
          content: 'You are an expert content creator specializing in blog enhancements. Generate content that perfectly matches the tone and style of the existing blog.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      model: 'gpt-5.4-mini',
      temperature: 0.8,
      max_tokens: 1000
    })
  }, { onContent, onComplete, onError });
};

/**
 * Generic function to create streaming connections
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @param {Object} callbacks - Callback functions
 * @returns {Promise} - Promise that resolves with the stream controller
 */
const createStreamConnection = async (url, options, { onContent, onComplete, onError }) => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      // Parse paywall / billing errors so callers can show the right UI
      if (response.status === 402) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        const paywallError = new Error(body.error || 'Insufficient credits');
        paywallError.isPaywall = true;
        paywallError.code = body.code || 'INSUFFICIENT_CREDITS';
        paywallError.availableCredits = body.availableCredits ?? 0;
        paywallError.requiredCredits = body.requiredCredits ?? 5;
        paywallError.topupUrl = body.topupUrl || '';
        throw paywallError;
      }

      // Anonymous-trial exhaustion: the container returns 403/409 with a
      // code the frontend uses to pop the signup modal instead of the
      // generic "something went wrong" message.
      if ([401, 403, 404, 409, 429].includes(response.status)) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        if (body.code && String(body.code).startsWith('ANON_')) {
          throw buildAnonymousLimitError(body);
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            if (onComplete) onComplete();
            break;
          }

          // Decode the chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines in the buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim() === '') continue;

            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                switch (data.type) {
                  case 'content':
                    if (onContent && data.content) {
                      onContent(data.content);
                    }
                    break;
                  case 'connection':
                    break;
                  case 'completion':
                    if (onComplete) {
                      onComplete(data.full_content || null);
                    }
                    return;
                  case 'error':
                    console.error('Stream error received:', data.error);
                    if (onError) onError(new Error(data.error || 'Unknown error'));
                    return;
                  case 'end':
                    if (onComplete) onComplete();
                    return;
                  default:
                    if (process.env.NODE_ENV === 'development') {
                      console.warn('Unknown stream data type:', data.type);
                    }
                    break;
                }
              } catch (parseError) {
                console.error('Error parsing stream data:', parseError);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('Stream processing error:', streamError);
        if (onError) onError(streamError);
      }
    };

    processStream();

    // Return a controller object to manage the stream
    return {
      stop: () => {
        reader.cancel();
      }
    };

  } catch (error) {
    console.error('Stream connection error:', error);
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Creates an appropriate prompt for enhancement generation
 * @param {Object} enhancement - The enhancement configuration
 * @param {string} blogContext - Current blog content
 * @returns {string} - The generated prompt
 */
const createEnhancementPrompt = (enhancement, blogContext) => {
  const contextPreview = blogContext.length > 500 
    ? blogContext.substring(0, 500) + '...' 
    : blogContext;

  const basePrompt = `Based on this blog content: "${contextPreview}"`;

  switch (enhancement.id) {
    case 'orderedList':
      return `${basePrompt}\n\nGenerate a relevant numbered list (maximum 3 items) that supports the main topic. Format as HTML with <ol> and <li> tags. Make it practical and actionable.`;
      
    case 'bulletList':
      return `${basePrompt}\n\nGenerate a relevant bullet point list (maximum 3 items) that highlights key points. Format as HTML with <ul> and <li> tags. Make it concise and impactful.`;
      
    case 'blockquote':
      return `${basePrompt}\n\nGenerate an inspiring and relevant quote that supports this blog topic. It could be from an expert, a well-known figure, or an insightful statement. Format as HTML with <blockquote> tags. Include attribution if appropriate.`;
      
    case 'ctaButton':
      return `${basePrompt}\n\nGenerate compelling call-to-action button text (2-4 words) that encourages reader engagement related to this topic. Return only the button text, no HTML formatting.`;
      
    case 'ctaBanner':
      return `${basePrompt}\n\nGenerate a compelling call-to-action banner message (10-15 words) that encourages reader engagement. Make it actionable and related to the blog topic. Return only the banner text, no HTML formatting.`;
      
    case 'visual':
      return `${basePrompt}\n\nDescribe a relevant visual (image, infographic, or diagram) that would enhance this blog post. Explain what it should show and how it supports the content. Be specific about the visual elements and layout.`;
      
    case 'dataVisualization':
      return `${basePrompt}\n\nSuggest a relevant data visualization (chart, graph, or statistic) that would support this blog topic. Describe what data should be shown, the chart type, and how it reinforces the blog's message.`;
      
    default:
      return `${basePrompt}\n\n${enhancement.prompt}`;
  }
};

/**
 * Non-streaming fallback for when streaming is not available
 * @param {string} h1 - The H1 heading for blog generation
 * @param {Object} brandVoice - Optional brand voice parameters
 * @returns {Promise<string>} - The generated blog content
 */
export const generateBlogNonStreaming = async (h1, brandVoice = {}, { priorCompletedGenerations = 0, trendData = '' } = {}) => {
  try {
    const headers = await getAuthHeaders();
    const isAnonymous = Boolean(headers['X-Anonymous-Session']) && !headers['Authorization'];

    // Build request payload with h1 and optional brand voice parameters
    const payload = {
      h1,
      priorCompletedGenerations,
      trend_data: trendData,
      ...brandVoice
    };

    const response = await fetch(`${API_BASE_URL}/api/seo/blog/complete`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 402) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        const paywallError = new Error(body.error || 'Insufficient credits');
        paywallError.isPaywall = true;
        paywallError.code = body.code || 'INSUFFICIENT_CREDITS';
        paywallError.availableCredits = body.availableCredits ?? 0;
        paywallError.requiredCredits = body.requiredCredits ?? 5;
        throw paywallError;
      }
      if ([401, 403, 404, 409, 429].includes(response.status)) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        if (body.code && String(body.code).startsWith('ANON_')) {
          throw buildAnonymousLimitError(body);
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (isAnonymous) {
      try { recordLocalBlogGeneration(); } catch { /* ignore */ }
    }
    return data.blog_content;
  } catch (error) {
    console.error('Non-streaming blog generation error:', error);
    throw error;
  }
};

/**
 * Non-streaming enhancement generation
 * @param {Object} enhancement - The enhancement object
 * @param {string} blogContext - Current blog content
 * @returns {Promise<string>} - The generated enhancement content
 */
export const generateEnhancementNonStreaming = async (enhancement, blogContext) => {
  try {
    const prompt = createEnhancementPrompt(enhancement, blogContext);
    const headers = await getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/chat/complete`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: 'You are an expert content creator specializing in blog enhancements. Generate content that perfectly matches the tone and style of the existing blog.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        model: 'gpt-5.4-mini',
        temperature: 0.8,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error('Non-streaming enhancement generation error:', error);
    throw error;
  }
};